import { useCallback, useRef } from "react";
import styles from "./FootDiagram.module.css";
import lateralLImg from "../../assets/footViews/lateralL.png";
import lateralRImg from "../../assets/footViews/lateralR.png";
import medialLImg  from "../../assets/footViews/medialL.png";
import medialRImg  from "../../assets/footViews/medialR.png";
import plantarLImg from "../../assets/footViews/plantarL.png";
import plantarRImg from "../../assets/footViews/plantarR.png";

// Plantar imgWrap is constrained to 54% of column width.
// Compensate so the dot looks the same size on all three view types.
const NARROW_RATIO = 0.54;
const BASE_DOT_PCT = 3; // % of a full-width (lateral/medial) image

const ROWS = [
  { label: "Lateral", narrow: false, L: { key: "lateralL", src: lateralLImg }, R: { key: "lateralR", src: lateralRImg } },
  { label: "Medial",  narrow: false, L: { key: "medialL",  src: medialLImg  }, R: { key: "medialR",  src: medialRImg  } },
  { label: "Plantar", narrow: true,  L: { key: "plantarL", src: plantarLImg }, R: { key: "plantarR", src: plantarRImg } },
];

function FootImageView({ viewKey, src, altLabel, sideLabel, narrow, dots = [], onChange }) {
  const dotPct = narrow ? BASE_DOT_PCT / NARROW_RATIO : BASE_DOT_PCT;

  // Track pointer-down position to distinguish taps from scrolls/drags.
  // onPointerUp fires on iOS/Android/desktop uniformly (Pointer Events API).
  // onClick on a <div> silently fails on iOS Safari without cursor:pointer.
  const pointerStart = useRef(null);

  const handlePointerDown = useCallback((e) => {
    pointerStart.current = { x: e.clientX, y: e.clientY };
  }, []);

  const handlePointerUp = useCallback((e) => {
    if (!pointerStart.current) return;
    const movedX = e.clientX - pointerStart.current.x;
    const movedY = e.clientY - pointerStart.current.y;
    pointerStart.current = null;

    // Ignore scroll/drag gestures (> 10px movement)
    if (Math.sqrt(movedX * movedX + movedY * movedY) > 10) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const xPct = ((e.clientX - rect.left)  / rect.width)  * 100;
    const yPct = ((e.clientY - rect.top)   / rect.height) * 100;
    const removeRadius = rect.width * (dotPct / 100) * 1.5;

    const removeIdx = dots.findIndex((dot) => {
      const dx = (dot.x - xPct) / 100 * rect.width;
      const dy = (dot.y - yPct) / 100 * rect.height;
      return Math.sqrt(dx * dx + dy * dy) < removeRadius;
    });

    if (removeIdx >= 0) {
      onChange(viewKey, dots.filter((_, i) => i !== removeIdx));
    } else {
      onChange(viewKey, [...dots, { x: xPct, y: yPct }]);
    }
  }, [dots, viewKey, onChange, dotPct]);

  return (
    <div className={styles.viewCell}>
      <span className={styles.mobileLabel}>{sideLabel}</span>
      <div
        className={`${styles.imgWrap}${narrow ? " " + styles.imgWrapNarrow : ""}`}
        style={{ "--dot-w": `${dotPct}%` }}
        onPointerDown={handlePointerDown}
        onPointerUp={handlePointerUp}
        role="img"
        aria-label={`${altLabel} – antippen zum Markieren`}
      >
        <img src={src} alt={altLabel} className={styles.img} draggable={false} />
        {dots.map((dot, i) => (
          <div
            key={i}
            className={styles.dot}
            style={{ left: `${dot.x}%`, top: `${dot.y}%` }}
          />
        ))}
      </div>
    </div>
  );
}

export default function FootDiagram({ value = {}, onChange }) {
  const getDots = (key) => value[key] ?? [];

  const handleChange = useCallback((viewKey, newDots) => {
    onChange({ ...value, [viewKey]: newDots });
  }, [value, onChange]);

  return (
    <div className={styles.wrap}>
      <p className={styles.instruction}>
        Klicken oder antippen Sie auf die betroffene Stelle. Zum Entfernen erneut klicken oder antippen.
      </p>
      <div className={styles.grid}>
        <div className={styles.colHeaders}>
          <div className={styles.rowLabelSpacer} />
          <div className={styles.colHeader}>Links</div>
          <div className={styles.colHeader}>Rechts</div>
        </div>
        {ROWS.map(({ label, narrow, L, R }) => (
          <div key={label} className={styles.gridRow}>
            <div className={styles.rowLabel}>{label}</div>
            <FootImageView
              viewKey={L.key} src={L.src} altLabel={`Links – ${label}`} sideLabel="Links"
              narrow={narrow} dots={getDots(L.key)} onChange={handleChange}
            />
            <FootImageView
              viewKey={R.key} src={R.src} altLabel={`Rechts – ${label}`} sideLabel="Rechts"
              narrow={narrow} dots={getDots(R.key)} onChange={handleChange}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
